import React, { createContext, useState, useContext, useEffect } from 'react';
import { login, logout, register, getStoredUser, getCurrentUser } from '../api';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Auth Context oluştur
const AuthContext = createContext(null);

// Context Provider bileşeni
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Uygulama başladığında kayıtlı kullanıcıyı kontrol et
  useEffect(() => {
    const loadUser = async () => {
      try {
        // AsyncStorage'dan kullanıcı bilgisini al
        const storedUser = await getStoredUser();
        
        if (storedUser) {
          setUser(storedUser);
          
          // Sunucudan en güncel kullanıcı bilgilerini al
          try {
            const currentUserData = await getCurrentUser();
            setUser(currentUserData.user);
            // Güncel kullanıcı bilgilerini AsyncStorage'a kaydet
            await AsyncStorage.setItem('user', JSON.stringify(currentUserData.user));
          } catch (err) {
            // Sunucudan bilgi alınamazsa token süresi dolmuş olabilir
            console.log('Oturum süresi dolmuş veya bağlantı hatası');
          }
        }
      } catch (err) {
        setError(err.message || 'Kullanıcı bilgileri yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    loadUser();
  }, []);
  
  // Login fonksiyonu
  const signIn = async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await login({ email, password });
      
      // 400/401 hataları için kontrol - API artık bu hataları throw etmiyor, obje olarak döndürüyor
      if (data && (data.status === 400 || data.status === 401)) {
        console.log('400/401 error caught in signIn');
        setLoading(false);
        setError(data.message || 'Giriş başarısız');
        return {
          success: false,
          message: data.message || 'Giriş başarısız oldu.',
          errors: data.errors || {},
          status: data.status
        };
      }
      
      // Başarılı giriş
      if (data.user) {
        setUser(data.user);
      }
      
      setLoading(false);
      return data;
    } catch (err) {
      console.error('AuthContext signIn error:', err);
      setError(err.message || 'Giriş başarısız');
      setLoading(false);
      
      // Hata nesnesini döndür, throw etme
      return {
        success: false,
        message: err.message || 'Giriş başarısız oldu. Lütfen tekrar deneyin.',
        errors: err.errors || {}
      };
    }
  };
  
  // Register fonksiyonu
  const signUp = async (userData) => {
    setLoading(true);
    setError(null);
    console.log('signUp başlatılıyor, userData:', userData);
    
    try {
      // register api artık 400/401 hatalarını throw etmek yerine nesne olarak döndürüyor
      const data = await register(userData);
      console.log('Register API response in AuthContext:', data);
      
      // 400/401 hatalarını doğrudan kontrol et
      if (data && (data.status === 400 || data.status === 401)) {
        console.log('400/401 error caught in AuthContext');
        setLoading(false);
        setError(data.message || 'Kayıt işlemi başarısız oldu.');
        return {
          success: false,
          message: data.message || 'Kayıt işlemi başarısız oldu.',
          errors: data.errors || {},
          status: data.status
        };
      }
      
      // Başarısızlık kontrolü
      if (!data || !data.success) {
        console.log('Register not successful:', data);
        setLoading(false);
        setError(data?.message || 'Kayıt işlemi başarısız oldu.');
        return { 
          success: false, 
          message: data?.message || 'Kayıt işlemi tamamlanamadı',
          errors: data?.errors || {}
        };
      }
      
      // Başarılı kayıt durumunda
      console.log('Register successful:', data);
      if (data.user && data.token) {
        setUser(data.user);
        await AsyncStorage.setItem('user', JSON.stringify(data.user));
        await AsyncStorage.setItem('token', data.token);
      }
      
      setLoading(false);
      return { 
        success: true, 
        user: data.user, 
        message: 'Kayıt başarılı!',
        token: data.token 
      };
    } catch (error) {
      console.error('Unexpected error in signUp:', error);
      setLoading(false);
      setError(error?.message || 'Kayıt sırasında beklenmeyen bir hata oluştu.');
      
      // Hata fırlatma, kontrollü şekilde döndür
      return {
        success: false,
        message: error?.message || 'Kayıt sırasında beklenmeyen bir hata oluştu.',
        errors: error?.errors || {}
      };
    }
  };
  
  // Logout fonksiyonu
  const signOut = async () => {
    setLoading(true);
    try {
      await logout();
      setUser(null);
      await AsyncStorage.removeItem('user');
      await AsyncStorage.removeItem('token');
    } catch (err) {
      setError(err.message || 'Logout başarısız');
      console.error('Çıkış yaparken hata:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Kullanıcı profilini güncelle
  const updateUserInfo = async (userData) => {
    // Locale güncelleme
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      // AsyncStorage'a güncellenen kullanıcı bilgilerini kaydet
      await AsyncStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };
  
  // Context değerlerini hazırla
  const value = {
    user,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    updateUserInfo,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Auth Context kullanım hook'u
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth hook, AuthProvider içinde kullanılmalıdır');
  }
  
  return context;
};
